import React, { useEffect, useMemo, useState, useRef } from "react";
import api, { API, getSession } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Search, Upload, Download, Pencil, Trash2, Briefcase, FileSpreadsheet, Loader2,
} from "lucide-react";
import {
  ASSET_CLASSES, ENTITY_TYPES, CURRENCIES, ASSET_CLASS_COLORS, fmtCurrency, fmtINR, fmtDate,
} from "@/lib/format";

const emptyForm = {
  asset_class: "Equity", name: "", entity_id: "none", currency: "INR",
  quantity: "", cost_value: "", market_value: "", as_of: "", notes: "",
};

export default function Holdings() {
  const [holdings, setHoldings] = useState([]);
  const [entities, setEntities] = useState([]);
  const [fx, setFx] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importRef = useRef(null);

  const entMap = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e.name])), [entities]);

  const load = async () => {
    setLoading(true);
    try {
      const [h, e, f] = await Promise.all([
        api.get("/holdings"), api.get("/entities"), api.get("/fx"),
      ]);
      setHoldings(h.data); setEntities(e.data);
      setFx(Object.fromEntries(f.data.map((r) => [r.currency, r.rate_to_inr])));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const inr = (h) => Number(h.market_value || 0) * Number(fx[h.currency] || 1);

  const filtered = useMemo(() => holdings.filter((h) => {
    if (search && !h.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (classFilter !== "all" && h.asset_class !== classFilter) return false;
    if (entityFilter !== "all" && (h.entity_id || "none") !== entityFilter) return false;
    return true;
  }), [holdings, search, classFilter, entityFilter]);

  const totalINR = filtered.reduce((s, h) => s + inr(h), 0);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({
      asset_class: h.asset_class, name: h.name, entity_id: h.entity_id || "none",
      currency: h.currency, quantity: h.quantity ?? "", cost_value: h.cost_value ?? "",
      market_value: h.market_value ?? "", as_of: h.as_of ? h.as_of.slice(0, 10) : "", notes: h.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Instrument name is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      entity_id: form.entity_id === "none" ? null : form.entity_id,
      quantity: form.quantity === "" ? null : Number(form.quantity),
      cost_value: Number(form.cost_value || 0),
      market_value: Number(form.market_value || 0),
      as_of: form.as_of || null,
    };
    try {
      if (editing) { await api.put(`/holdings/${editing.id}`, payload); toast.success("Holding updated"); }
      else { await api.post("/holdings", payload); toast.success("Holding added"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (h) => {
    try { await api.delete(`/holdings/${h.id}`); toast.success("Holding deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const downloadTemplate = () => {
    const url = `${API}/holdings/template`;
    fetch(url, { headers: { Authorization: `Bearer ${getSession()}` } })
      .then((r) => r.blob()).then((b) => {
        const u = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = u; a.download = "holdings_template.csv"; a.click(); URL.revokeObjectURL(u);
      });
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/holdings/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.imported} of ${data.total_rows} rows`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); if (importRef.current) importRef.current.value = ""; }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Portfolio" subtitle="All holdings across entities and currencies">
        <input ref={importRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={doImport} data-testid="holdings-import-input" />
        <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="holdings-import-button">
          <Upload className="h-4 w-4 mr-2" /> Import
        </Button>
        <Button onClick={openAdd} className="bg-[var(--primary-deep-green)] hover:bg-[var(--primary-deep-green-hover)] text-white" data-testid="holdings-add-button">
          <Plus className="h-4 w-4 mr-2" /> Add Holding
        </Button>
      </PageHeader>

      {/* Toolbar */}
      <Card className="bg-white border-border p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search instruments…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="holdings-search-input" />
          </div>
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger className="w-full md:w-52" data-testid="holdings-filter-asset-class"><SelectValue placeholder="Asset class" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All asset classes</SelectItem>
              {ASSET_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-full md:w-48" data-testid="holdings-filter-entity"><SelectValue placeholder="Entity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="none">Unassigned</SelectItem>
              {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
      ) : holdings.length === 0 ? (
        <Card className="bg-white border-border">
          <EmptyState icon={Briefcase} title="No holdings yet"
            description="Add your first holding manually, or import a CSV / Excel file to populate your portfolio."
            action={<div className="flex gap-2"><Button onClick={openAdd} className="bg-[var(--primary-deep-green)] hover:bg-[var(--primary-deep-green-hover)] text-white">Add Holding</Button><Button variant="outline" onClick={() => setImportOpen(true)}>Import File</Button></div>} />
        </Card>
      ) : (
        <Card className="bg-white border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="holdings-table">
              <thead className="bg-[var(--sand)]/60">
                <tr className="text-left text-xs font-semibold text-slate-600">
                  <th className="px-4 py-3">Instrument</th>
                  <th className="px-4 py-3">Asset Class</th>
                  <th className="px-4 py-3">Entity</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3 text-right">Market Value</th>
                  <th className="px-4 py-3 text-right">Value (INR)</th>
                  <th className="px-4 py-3">As of</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((h) => (
                  <tr key={h.id} className="border-t border-border hover:bg-[var(--sand)]/40">
                    <td className="px-4 py-3 font-medium text-[var(--ink)]">{h.name}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs text-slate-600"><span className="h-2 w-2 rounded-full" style={{ background: ASSET_CLASS_COLORS[h.asset_class] }} />{h.asset_class}</span></td>
                    <td className="px-4 py-3 text-slate-600">{entMap[h.entity_id] || <span className="text-muted-foreground">Unassigned</span>}</td>
                    <td className="px-4 py-3 text-right font-data tabular-nums text-slate-600">{h.quantity ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-data tabular-nums">{fmtCurrency(h.market_value, h.currency)}</td>
                    <td className="px-4 py-3 text-right font-data tabular-nums font-medium">{fmtINR(inr(h))}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(h.as_of)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(h)} data-testid={`holdings-edit-${h.id}`}><Pencil className="h-4 w-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--destructive)]" data-testid={`holdings-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete holding?</AlertDialogTitle><AlertDialogDescription>This will permanently remove “{h.name}” from your portfolio.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(h)} className="bg-[var(--destructive)] hover:bg-[var(--destructive)]/90">Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-[var(--sand)]/30 font-medium">
                  <td className="px-4 py-3" colSpan={5}>{filtered.length} holding(s)</td>
                  <td className="px-4 py-3 text-right font-data tabular-nums" data-testid="holdings-total-inr">{fmtINR(totalINR)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Holding" : "Add Holding"}</DialogTitle>
            <DialogDescription>Enter the instrument details. Values are recorded in the holding's currency and converted to INR.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Asset Class</Label>
              <Select value={form.asset_class} onValueChange={(v) => setForm({ ...form, asset_class: v })}>
                <SelectTrigger data-testid="form-asset-class"><SelectValue /></SelectTrigger>
                <SelectContent>{ASSET_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Instrument Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. HDFC Bank" data-testid="form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Entity</Label>
              <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v })}>
                <SelectTrigger data-testid="form-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="form-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Quantity (optional)</Label>
              <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="form-quantity" />
            </div>
            <div className="space-y-1.5">
              <Label>As-of date</Label>
              <Input type="date" value={form.as_of} onChange={(e) => setForm({ ...form, as_of: e.target.value })} data-testid="form-as-of" />
            </div>
            <div className="space-y-1.5">
              <Label>Cost Value ({form.currency})</Label>
              <Input type="number" value={form.cost_value} onChange={(e) => setForm({ ...form, cost_value: e.target.value })} data-testid="form-cost" />
            </div>
            <div className="space-y-1.5">
              <Label>Market Value ({form.currency})</Label>
              <Input type="number" value={form.market_value} onChange={(e) => setForm({ ...form, market_value: e.target.value })} data-testid="form-market" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[var(--primary-deep-green)] hover:bg-[var(--primary-deep-green-hover)] text-white" data-testid="form-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Add Holding"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Import Holdings</DialogTitle>
            <DialogDescription>Upload a CSV or Excel file. Columns: asset_class, name, entity, currency, quantity, cost_value, market_value, as_of.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <button onClick={downloadTemplate} className="w-full flex items-center justify-center gap-2 rounded-lg border border-border bg-[var(--sand)]/40 hover:bg-[var(--sand)] transition-colors py-3 text-sm font-medium" data-testid="download-template-button">
              <Download className="h-4 w-4" /> Download CSV Template
            </button>
            <button onClick={() => importRef.current?.click()} disabled={importing}
              className="w-full rounded-xl border-2 border-dashed border-border hover:border-[var(--accent-brass)] hover:bg-[var(--sand)]/40 transition-colors py-8 flex flex-col items-center gap-2" data-testid="import-choose-file">
              {importing ? <Loader2 className="h-7 w-7 animate-spin text-[var(--accent-brass)]" /> : <FileSpreadsheet className="h-7 w-7 text-[var(--accent-brass)]" />}
              <span className="text-sm font-medium">{importing ? "Importing…" : "Choose CSV / Excel file"}</span>
            </button>
            {importResult && (
              <div className="rounded-lg border border-border p-4 text-sm">
                <div className="flex items-center gap-2 text-[var(--accent-brass)] font-medium"><FileSpreadsheet className="h-4 w-4" /> Imported {importResult.imported} of {importResult.total_rows} rows</div>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto text-xs text-[var(--destructive)] space-y-1">
                    {importResult.errors.map((er, i) => <div key={i}>Row {er.row}: {er.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
